import { customer_sheet_fields_convert_attr } from "@lhd/features/Customers/ManagementCustomerSheet/ManagementUserSheetList/utility";
import { task_sheet_fields_convert_attr } from "@lhd/features/ProjectManagement/ManagementTaskSheet/ManagementUserTaskList/utility";
import { project_sheet_fields_convert_attr } from "@lhd/features/ProjectManagement/ProjectInformation/ManagementProjectListSheet/ManagementUserTaskList/utility";
import { arrangment_special_fields_convert_attr } from "@lhd/features/Salary/Arrangements/ArrangementsList/utility";
import { deduction_special_fields_convert_attr } from "@lhd/features/Salary/Deductions/DeductionsList/utility";
import { expense_special_fields_convert_attr } from "@lhd/features/Salary/Expenses/ExpensesList/utility";
import { provision_sheet_special_field_convert_attr } from "@lhd/features/Salary/Provisions/ProvisionsList/utility";
import { staff_tax_sheet_special_field_convert_attr } from "@lhd/features/Salary/StaffTaxSheets/StaffTaxSheetsList/utility";
import { transfer_special_fields_convert_attr } from "@lhd/features/Salary/Transfers/TransfersList/utility";
import { work_special_fields_convert_attr } from "@lhd/features/Salary/Works/WorksList/utility";
import { isJPDateLabel } from "@lhd/utility";
import { ReactGrid } from "@silevis/reactgrid";
import Decimal from "decimal.js";
import { get, isNaN, uniq } from "lodash";
import moment from "moment";

export const MONTHLY = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const HEIGHT_HEADER_GROUP = 45;

/**
 * Dynamic date field patterns used across the application
 * Supports fields with index like: worker_dependants.0.birthday, name_history.1.start_date, etc.
 */
export const DYNAMIC_DATE_FIELD_PATTERNS = [
  /^worker_dependants\.\d+\.birthday$/,
  /^name_history\.\d+\.start_date$/,
  /^qualifications\.\d+\.start_date$/,
  /^education_work\.\d+\.start_date$/,
  /^job_details\.\d+\.start_date$/,
  /^employment\.\d+\.start_date$/,
  /^regular_insurance\.\d+\.insurance_get_date$/,
  /^regular_insurance\.\d+\.insurance_lost_date$/,
  /^unemployment_insurance\.\d+\.insurance_get_date$/,
];

/**
 * Check if a field matches dynamic date field patterns
 * @param fieldKey - The field key to check (e.g., "worker_dependants.0.birthday")
 * @returns true if the field matches any dynamic date pattern
 */
export const matchesDynamicDatePattern = (fieldKey: string): boolean => {
  return DYNAMIC_DATE_FIELD_PATTERNS.some((pattern) => pattern.test(fieldKey));
};

export const optionFilterCellEmpty = {
  label: "(空白)",
  value: "special_empty_value",
};

export const textToNumber = (text: string | number) => {
  if (typeof text === "number") return text;
  if (typeof text !== "string" || !text.trim()) return NaN;
  const number = Number(text);
  return number;
};

export function sumAllColumns(data: ReactGrid["state"]["selectedRanges"]) {
  const total: Record<string, Decimal> = {};
  let count = 0;
  let countNumber = 0;
  let min: number | null = null;
  let max: number | null = null;

  const distinctValues = new Set<string>();

  data.forEach((range) => {
    range.columns.forEach((col) => {
      if (!col || col.idx === undefined) return;
      if (!total[col.idx.toString()])
        total[col.idx.toString()] = new Decimal(0);

      range.rows.forEach((row) => {
        if (!row || !row.cells) return;
        const cell = row.cells[col.idx];
        if (cell && col.columnId !== "index") {
          const cellAs = cell as any;
          const valRaw = cellAs.value || cellAs.text;
          const val = textToNumber(valRaw);
          const hasValue =
            (cellAs.text !== undefined && cellAs.text !== "") ||
            (cellAs.value !== undefined && cellAs.value !== null);

          if (hasValue) {
            count++;
            distinctValues.add(String(valRaw));
          }
          if (isNaN(val)) return;

          countNumber++;
          const valNumber = val || 0;
          if (max === null) {
            max = valNumber;
          }
          if (min === null) {
            min = valNumber;
          }
          max = Math.max(max, valNumber);
          min = Math.min(min, valNumber);
          total[col.idx.toString()] = total[col.idx.toString()].add(
            val || valNumber,
          );
        }
      });
    });
  });

  return {
    count,
    distinctCount: distinctValues.size,
    total,
    countNumber,
    max,
    min,
  };
}

export const parseColumnId = (columnId: string, additionalField?: string[]) => {
  const salaryFieldsConvert = [
    ...staff_tax_sheet_special_field_convert_attr,
    ...provision_sheet_special_field_convert_attr,
    ...expense_special_fields_convert_attr,
    ...transfer_special_fields_convert_attr,
    ...work_special_fields_convert_attr,
    ...arrangment_special_fields_convert_attr,
    ...deduction_special_fields_convert_attr,
    ...task_sheet_fields_convert_attr,
    ...project_sheet_fields_convert_attr,
    ...customer_sheet_fields_convert_attr,
  ];
  const parseColumnActive = [
    ...uniq(salaryFieldsConvert),
    ...(additionalField || []),
  ].includes(columnId)
    ? `${columnId}_attr`
    : columnId;

  return parseColumnActive;
};
export const checkEmptyRow = (item: any, columnId: string) => {
  if (columnId === "is_admin") {
    const keys = Object.keys(item).filter(
      (key) => !["index", "isCreate", "id"].includes(key),
    );
    return keys.every((key) => item[key] === "" || item[key] == null);
  } else {
    const keys = Object.keys(item).filter(
      (key) => !["index", "isCreate", "id", "is_admin"].includes(key),
    );
    return keys.every((key) => item[key] === "" || item[key] == null);
  }
};
const renderLabelCheckbox = (isChecked: boolean) => {
  return isChecked ? "管理" : "(空白)";
};

const isDateHierarchyField = (
  columnId: string,
  dateFieateHierarchyFields?: string[],
): boolean => {
  if (
    dateFieateHierarchyFields &&
    dateFieateHierarchyFields.includes(columnId)
  ) {
    return true;
  }

  // Check for dynamic patterns
  return matchesDynamicDatePattern(columnId);
};

export const parseOptions = (
  data: any[],
  columnId: string,
  dateFieateHierarchyFields?: string[],
  getValue?: (value: string, type: string) => string,
  isPreferUseGetValue?: boolean,
) => {
  const orginalColumnId = columnId?.includes("_attr")
    ? columnId.replace("_attr", "")
    : columnId;
  const filteredOriginalData = (data || []).filter(
    (item) => !checkEmptyRow(item, orginalColumnId),
  );

  let options: { value: string; label: string; id: number }[] = [];
  let isShowEmptyDate = false;
  const isDateField = isDateHierarchyField(
    orginalColumnId,
    dateFieateHierarchyFields,
  );

  options = filteredOriginalData
    ?.filter(
      (item) =>
        item.index !== "合計金" &&
        item.worker_no !== "合計金額" &&
        !item.isTotal &&
        item.id !== undefined,
    )
    .map((item) => {
      const label = get(item, orginalColumnId);
      if (isPreferUseGetValue && getValue && label != null) {
        const computed = getValue(label, orginalColumnId);
        return {
          id: item.id,
          value: label,
          label: computed,
        };
      }
      return isDateField
        ? {
            id: item.id,
            value: label
              ? isJPDateLabel(label)
                ? label
                : moment(label).format("YYYY年MM月DD日")
              : null,
            label: label
              ? isJPDateLabel(label)
                ? label
                : moment(label).format("YYYY年MM月DD日")
              : "",
          }
        : {
            id: item.id,
            value: label,
            label:
              getValue && typeof label === "string"
                ? getValue(label, orginalColumnId)
                : orginalColumnId === "is_admin"
                ? renderLabelCheckbox(label)
                : label,
          };
    });

  const hasEmptyValue = options.some((item) => {
    return (item.value == null || item.value === "") && !!item?.id;
  });

  options = options.filter((item) => {
    return item.value != null && item.value !== "";
  });

  options = options.filter(
    (item, index, self) =>
      index === self.findIndex((option) => option.label === item.label),
  );

  if (hasEmptyValue) {
    isShowEmptyDate = true;
  }
  return { options, isShowEmptyDate };
};
